import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ChevronLeft, ChevronRight, Plus, X, CalendarDays, Users, Share2, Copy, Check,
} from "lucide-react";
import {
  useTarefasByPeriodo, useFuncionarios, useAtividades,
  useCreateTarefa,
} from "@/hooks/use-equipe";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { isSemQuinzena, SEM_QUINZENA_MSG } from "@/lib/equipe-errors";
import { useAuth } from "@/lib/auth-context";
import { format, startOfWeek, addDays, addWeeks, getISOWeek } from "date-fns";
import { ptBR } from "date-fns/locale";
import { TarefaDiariaSheet, type TarefaParaEditar, type TarefaStatus } from "@/components/equipe/TarefaDiariaSheet";
import { SemQuinzenaBanner } from "@/components/equipe/SemQuinzenaBanner";
import { SetorChipPicker } from "@/components/equipe/SetorChipPicker";
import { formatSetores } from "@/lib/setores";
import type { Tarefa } from "@/../../shared/schema";


import { PageHeader } from "@/components/layout/page-header";
import { Segmented } from "@/components/layout/segmented";
import { avatarCor } from "@/lib/avatar-cor";
// ─── Helpers ──────────────────────────────────────────────────────────────────

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const DIAS_ABREV = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function diaDaSemanaCurto(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return DIAS_ABREV[d.getDay()];
}

// Status config
const STATUS_CFG: Record<string, { label: string; chip: string; border: string }> = {
  PENDENTE:     { label: "A fazer",      chip: "bg-muted text-muted-foreground",   border: "border-l-muted-foreground" },
  EM_ANDAMENTO: { label: "Em andamento", chip: "bg-atencao-suave text-atencao",  border: "border-l-amber-400" },
  CONCLUIDA:    { label: "Concluída",    chip: "bg-sucesso-suave text-sucesso",  border: "border-l-green-500" },
  FALTOU:       { label: "Faltou",       chip: "bg-erro-suave text-erro",      border: "border-l-red-400" },
  CANCELADA:    { label: "Cancelada",    chip: "bg-muted text-muted-foreground",   border: "border-l-border" },
};

interface LinhaAtiv { setorIds: string[]; atividadeId: string; }
const emptyLinha = (): LinhaAtiv => ({ setorIds: [], atividadeId: "" });

// Agrupamento de tarefas: (funcionario, data, atividade) → um card com N setores
interface TarefaGroup {
  key: string;
  tarefas: Tarefa[];
  funcionarioId: string;
  atividadeId: string | null;
  data: string;
  status: string;
  setorIds: (string | null)[];
}

function groupTarefas(list: Tarefa[]): TarefaGroup[] {
  const map = new Map<string, TarefaGroup>();
  for (const t of list) {
    const key = `${t.funcionarioId}|${t.data}|${t.atividadeId ?? ""}`;
    if (!map.has(key)) {
      map.set(key, { key, tarefas: [], funcionarioId: t.funcionarioId, atividadeId: t.atividadeId ?? null, data: t.data, status: t.status, setorIds: [] });
    }
    const g = map.get(key)!;
    g.tarefas.push(t);
    // Collect setorIds from t.setorIds (multi-setor), fallback to t.setorId
    const ids: (string | null)[] = t.setorIds?.length ? t.setorIds : (t.setorId ? [t.setorId] : []);
    for (const id of ids) {
      if (!g.setorIds.includes(id)) g.setorIds.push(id);
    }
  }
  return Array.from(map.values());
}

// ─── Componente principal ──────────────────────────────────────────────────────

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook).
const PADRAO_PLANO = { visao: "dia" };
const PERMITIDOS_PLANO = { visao: ["dia", "colab"] } as const;

export default function PlanoDaSemana() {
  const { toast } = useToast();
  const { user } = useAuth();
  const orgName = user?.orgName ?? "Fazenda";

  // Estado de navegação e view
  const { estado: filtrosUrl, definir } = useEstadoNaUrl(PADRAO_PLANO, PERMITIDOS_PLANO);
  const view = filtrosUrl.visao as "dia" | "colab";
  const setView = (v: "dia" | "colab") => definir({ visao: v });
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDay, setSelectedDay] = useState<number>(() => {
    const dow = new Date().getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
    // Mapear para índice 0–5 (Seg=0, Sáb=5); Dom cai em Seg (0)
    return dow === 0 ? 0 : Math.min(dow - 1, 5);
  });
  const [selectedColabId, setSelectedColabId] = useState<string | null>(null);
  const [activeTarefasDia, setActiveTarefasDia] = useState<TarefaParaEditar[]>([]);

  // Calcular dias da semana: Seg–Sáb
  const weekStart = startOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 1 });
  const days = Array.from({ length: 6 }, (_, i) => addDays(weekStart, i));
  const dataInicio = format(days[0], "yyyy-MM-dd");
  const dataFim = format(days[5], "yyyy-MM-dd");
  const semanaNum = getISOWeek(weekStart);

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const todayIdx = (() => {
    const dow = new Date().getDay();
    return dow === 0 ? 0 : Math.min(dow - 1, 5);
  })();

  // Queries
  const { data: tarefas = [], isLoading } = useTarefasByPeriodo(dataInicio, dataFim);
  const { data: funcionarios = [] } = useFuncionarios();
  const { data: atividades = [] } = useAtividades();
  const { data: setoresRaw = [] } = useQuery<{ id: string; nome: string; ativo: boolean }[]>({ queryKey: ["/api/setores"] });
  const setores = setoresRaw.filter(s => s.ativo !== false).sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  const createTarefa = useCreateTarefa();

  const funcAtivos = funcionarios.filter(f => f.ativo !== false);
  const atividadeMap = new Map(atividades.map(a => [a.id, a]));
  const funcMap = new Map(funcionarios.map(f => [f.id, f]));
  const setorMap = new Map(setoresRaw.map(s => [s.id, s.nome]));

  // Selecionar primeiro colaborador ao montar / ao mudar lista
  useEffect(() => {
    if (view === "colab" && funcAtivos.length > 0 && !selectedColabId) {
      setSelectedColabId(funcAtivos[0].id);
    }
  }, [view, funcAtivos, selectedColabId]);

  // Abrir bottom sheet com todas as tarefas do dia para o funcionário
  const openSheet = (t: Tarefa) => {
    const doDia = tarefas.filter(x => x.funcionarioId === t.funcionarioId && x.data === t.data);
    const mapped: TarefaParaEditar[] = doDia.map(x => {
      const func = funcMap.get(x.funcionarioId);
      return {
        id: x.id,
        data: x.data,
        funcionarioId: x.funcionarioId,
        funcionarioNome: func?.apelido ?? func?.nome ?? "?",
        atividadeId: x.atividadeId ?? "",
        atividadeNome: atividadeMap.get(x.atividadeId ?? "")?.nome ?? "—",
        setorId: x.setorId ?? null,
        setorIds: x.setorIds?.length ? x.setorIds : (x.setorId ? [x.setorId] : []),
        setorNome: setorMap.get(x.setorId ?? "") ?? "",
        tipo: (x as any).tipo ?? "NORMAL",
        status: x.status as TarefaStatus,
        valor: String(parseFloat(String((x as any).valor ?? 0))),
        valorAdicional: (x as any).valorAdicional ?? null,
        comentario: (x as any).comentario ?? null,
      };
    });
    setActiveTarefasDia(mapped);
  };

  // ─── Compartilhar plano ────────────────────────────────────────────────────

  const [shareColabId, setShareColabId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const generateShareMessage = (funcId: string): string => {
    const f = funcMap.get(funcId);
    if (!f) return "";
    const nome = f.apelido ?? f.nome.split(" ")[0];

    const tColab = tarefas.filter(t => t.funcionarioId === funcId);

    const allSetores = setoresRaw.filter(s => s.ativo !== false);
    const formatGroups = (list: Tarefa[]) =>
      groupTarefas(list)
        .sort((a, b) => (a.atividadeId ?? "").localeCompare(b.atividadeId ?? ""))
        .map(g => {
          const ativ = atividadeMap.get(g.atividadeId ?? "")?.nome ?? "—";
          const label = formatSetores(g.setorIds.filter(Boolean) as string[], allSetores);
          return `• ${ativ}${label ? ` — ${label}` : ""}`;
        })
        .join("\n");

    const todayTarefas = tColab.filter(t => t.data === todayStr);
    const futureDays = days.filter(d => format(d, "yyyy-MM-dd") > todayStr);
    const futurePorDia = futureDays
      .map(d => {
        const dStr = format(d, "yyyy-MM-dd");
        const list = tColab.filter(t => t.data === dStr);
        if (list.length === 0) return null;
        const label = `${diaDaSemanaCurto(dStr)} ${format(d, "dd/MM")}`;
        return groupTarefas(list)
          .sort((a, b) => (a.atividadeId ?? "").localeCompare(b.atividadeId ?? ""))
          .map(g => {
            const ativ = atividadeMap.get(g.atividadeId ?? "")?.nome ?? "—";
            const setorLabel = formatSetores(g.setorIds.filter(Boolean) as string[], allSetores);
            return `• ${label}: ${ativ}${setorLabel ? ` — ${setorLabel}` : ""}`;
          })
          .join("\n");
      })
      .filter(Boolean) as string[];

    let msg = `Bom dia ${nome}!\nSeu plano da semana — ${orgName}\n`;
    if (todayTarefas.length > 0) {
      const todayLabel = `${diaDaSemanaCurto(todayStr)} ${format(new Date(), "dd/MM")}`;
      msg += `\n*HOJE (${todayLabel})*\n${formatGroups(todayTarefas)}`;
    }
    if (futurePorDia.length > 0) {
      msg += `\n\n*PRÓXIMOS DIAS*\n${futurePorDia.join("\n")}`;
    }
    msg += `\n\nQualquer dúvida, fala comigo. Bom trabalho!`;
    return msg.trim();
  };

  const shareMessage = shareColabId ? generateShareMessage(shareColabId) : "";

  const handleCopy = () => {
    navigator.clipboard.writeText(shareMessage).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleWhatsApp = () => {
    const func = funcMap.get(shareColabId ?? "");
    const phone = (func as any)?.telefone?.replace(/\D/g, "");
    const url = phone
      ? `https://wa.me/55${phone}?text=${encodeURIComponent(shareMessage)}`
      : `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(url, "_blank");
  };

  // ─── Dialog nova tarefa ────────────────────────────────────────────────────
  const [addOpen, setAddOpen] = useState(false);
  const [novaData, setNovaData] = useState(format(new Date(), "yyyy-MM-dd"));
  const [novaFuncId, setNovaFuncId] = useState("");
  const [novaStatus, setNovaStatus] = useState<TarefaStatus>("PENDENTE");
  const [linhas, setLinhas] = useState<LinhaAtiv[]>([emptyLinha()]);
  const [novaObs, setNovaObs] = useState("");
  const [novaValor, setNovaValor] = useState("");
  const [novoTipo, setNovoTipo] = useState<string>("NORMAL");

  const openDialog = (data?: string, funcId?: string) => {
    setNovaData(data ?? format(new Date(), "yyyy-MM-dd"));
    const func = funcionarios.find(f => f.id === funcId);
    setNovaFuncId(funcId ?? "");
    setNovaValor(func?.valorDiaria ? String(parseFloat(func.valorDiaria)) : "");
    setLinhas([emptyLinha()]);
    setNovaObs("");
    setNovaStatus("PENDENTE");
    setNovoTipo("NORMAL");
    setAddOpen(true);
  };

  const handleSelectFunc = (funcId: string) => {
    const func = funcionarios.find(f => f.id === funcId);
    setNovaFuncId(funcId);
    // Regime manda no tipo: mensalista só gera registro (R$ 0), diarista nunca gera
    // registro. Alinhar aqui evita o 409 REGIME_* do servidor com o form preenchido.
    if (((func as any)?.tipoRemuneracao ?? "DIARISTA") === "MENSALISTA") {
      setNovoTipo("MENSALISTA");
      setNovaValor("");
      return;
    }
    if (novoTipo === "MENSALISTA") setNovoTipo("NORMAL");
    if (func?.valorDiaria) setNovaValor(String(parseFloat(func.valorDiaria)));
  };

  /** Regime do funcionário escolhido no formulário de nova tarefa. */
  const novoFuncEhMensalista =
    ((funcionarios.find(f => f.id === novaFuncId) as any)?.tipoRemuneracao ?? "DIARISTA") === "MENSALISTA";

  const updateLinhaAtiv = (idx: number, atividadeId: string) =>
    setLinhas(ls => ls.map((l, i) => i === idx ? { ...l, atividadeId } : l));
  const updateLinhaSetorIds = (idx: number, setorIds: string[]) =>
    setLinhas(ls => ls.map((l, i) => i === idx ? { ...l, setorIds } : l));
  const addLinha = () => setLinhas(ls => [...ls, emptyLinha()]);
  const removeLinha = (idx: number) => setLinhas(ls => ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls);

  const handleAddTarefas = async () => {
    const isFaltouStatus = novaStatus === "FALTOU";
    const validas = linhas.filter(l => l.atividadeId);
    // FALTOU: atividade opcional — se não houver linha válida, cria uma sem atividade
    const toCreate = (isFaltouStatus && validas.length === 0)
      ? [{ setorIds: [] as string[], atividadeId: "" }]
      : validas;
    if (!novaFuncId || toCreate.length === 0) return;
    try {
      await Promise.all(toCreate.map((l, i) =>
        createTarefa.mutateAsync({
          funcionarioId: novaFuncId,
          atividadeId: l.atividadeId || undefined,
          setorIds: l.setorIds,
          setorId: l.setorIds[0] ?? undefined,
          data: novaData,
          status: novaStatus,
          tipo: novoTipo as "NORMAL" | "MEIA_DIARIA" | "HORA_EXTRA" | "EMPREITA" | "MENSALISTA",
          valor: novoTipo === "MENSALISTA" || isFaltouStatus ? "0" : i === 0 ? (novaValor || "0") : "0",
          observacoes: novaObs.trim() || undefined,
        })
      ));
      setAddOpen(false);
      toast({ title: isFaltouStatus ? "Falta registrada!" : `${validas.length} tarefa(s) adicionada(s)!` });
    } catch (err) {
      if (isSemQuinzena(err)) {
        toast({ title: "Crie a quinzena primeiro", description: SEM_QUINZENA_MSG, variant: "destructive" });
      } else {
        toast({ title: "Erro ao adicionar tarefa", variant: "destructive" });
      }
    }
  };

  // ─── VIEW POR DIA ──────────────────────────────────────────────────────────

  const dayTarefas = tarefas.filter(t => t.data === format(days[selectedDay], "yyyy-MM-dd"));

  const statusCounts = dayTarefas.reduce<Record<string, number>>((acc, t) => {
    acc[t.status] = (acc[t.status] ?? 0) + 1;
    return acc;
  }, {});

  // ─── VIEW POR COLABORADOR ──────────────────────────────────────────────────

  const colabTarefas = tarefas.filter(t => t.funcionarioId === selectedColabId);
  const colabByDay = days.map(d => {
    const dateStr = format(d, "yyyy-MM-dd");
    return { date: d, dateStr, tarefas: colabTarefas.filter(t => t.data === dateStr) };
  }).filter(g => g.tarefas.length > 0);

  const colabTotal = colabTarefas.length;
  const colabFeitas = colabTarefas.filter(t => t.status === "CONCLUIDA").length;
  const colabFaltou = colabTarefas.filter(t => t.status === "FALTOU").length;

  // ─── Sub-componentes de card (reutilizados em mobile e desktop) ───────────

  const renderCardDia = (t: Tarefa, opts: { showAvatar: boolean }) => {
    const func = funcMap.get(t.funcionarioId);
    const ativ = atividadeMap.get(t.atividadeId ?? "");
    const nome = func?.apelido ?? func?.nome ?? "?";
    const cor = avatarCor(func?.id);
    const cfg = STATUS_CFG[t.status] ?? STATUS_CFG.PENDENTE;
    const setor = setorMap.get(t.setorId ?? "") ?? "";
    const opacityCls = t.status === "CONCLUIDA" ? "opacity-70" : t.status === "FALTOU" ? "opacity-65" : "";
    return (
      <div
        key={t.id}
        onClick={() => openSheet(t)}
        className={`bg-card border border-border rounded-r-xl p-2.5 flex items-start gap-2 cursor-pointer hover:shadow-sm transition-shadow border-l-[3px] ${cfg.border} ${opacityCls}`}
      >
        {opts.showAvatar && (
          <span
            className="inline-flex items-center justify-center h-7 w-7 rounded-full text-white text-[10px] font-bold shrink-0 mt-0.5"
            style={{ backgroundColor: cor }}
          >
            {initials(nome)}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-xs text-foreground truncate">{ativ?.nome ?? "—"}</p>
          <p className="text-[10px] text-muted-foreground truncate">
            {opts.showAvatar ? `${nome}${setor ? ` · ${setor}` : ""}` : setor || "—"}
          </p>
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 mt-0.5 ${cfg.chip}`}>
          {cfg.label}
        </span>
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-background">
      {/* ── TopBar (compartilhado mobile + desktop) ── */}
      <div className="bg-card border-b px-4 pt-4 pb-0">
        {/* Título + nav semana */}
        <div className="mb-3">
          <PageHeader
            titulo="Plano da semana"
            acao={
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setWeekOffset(w => w - 1)}
                  className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-semibold text-foreground px-1 tabular-nums">S{semanaNum}</span>
                <button
                  onClick={() => setWeekOffset(w => w + 1)}
                  className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            }
          />
        </div>

        {/* Toggle Por dia / Por colaborador */}
        <Segmented
          className="mb-3"
          valor={view}
          onChange={v => {
            setView(v);
            if (v === "colab" && funcAtivos.length > 0 && !selectedColabId) setSelectedColabId(funcAtivos[0].id);
          }}
          opcoes={[
            { id: "dia", label: "Por dia", icone: CalendarDays },
            { id: "colab", label: "Por colaborador", icone: Users },
          ]}
        />

        {/* Tabs de dia — só mobile, só na view Por dia */}
        {view === "dia" && (
          <div className="flex gap-0.5 overflow-x-auto pb-0 -mx-1 px-1 md:hidden">
            {days.map((d, i) => {
              const dStr = format(d, "yyyy-MM-dd");
              const isToday = dStr === todayStr;
              const isSelected = i === selectedDay;
              return (
                <button
                  key={dStr}
                  onClick={() => setSelectedDay(i)}
                  className={`flex flex-col items-center px-3 py-2 rounded-t-lg min-w-[52px] transition-all border-b-2 ${
                    isSelected
                      ? "border-sucesso bg-card text-sucesso"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <span className="text-[10px] font-medium uppercase">{format(d, "EEE", { locale: ptBR })}</span>
                  {isToday ? (
                    <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                      {format(d, "d")}
                    </span>
                  ) : (
                    <span className="text-sm font-semibold">{format(d, "d")}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 pt-4 empty:hidden">
          <SemQuinzenaBanner />
        </div>
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
          </div>
        ) : (
          <>
            {/* ════════════════════════════════════════
                DESKTOP (md e acima) — layout kanban em colunas
                ════════════════════════════════════════ */}
            <div className="hidden md:block p-4">
              {view === "dia" ? (
                /* Desktop — Por dia */
                <div className="overflow-x-auto">
                  <div className="flex gap-4 min-w-max pb-4">
                    {days.map((d) => {
                      const dStr = format(d, "yyyy-MM-dd");
                      const isToday = dStr === todayStr;
                      const grupos = groupTarefas(tarefas.filter(t => t.data === dStr))
                        .sort((a, b) => a.funcionarioId.localeCompare(b.funcionarioId));
                      return (
                        <div key={dStr} className="w-64 flex-shrink-0">
                          {/* Header da coluna */}
                          <div className={`flex items-center justify-between mb-3 px-1 pb-2 border-b-2 ${isToday ? "border-sucesso" : "border-border"}`}>
                            <span className={`text-sm font-semibold capitalize ${isToday ? "text-sucesso" : "text-muted-foreground"}`}>
                              {format(d, "EEE dd/MM", { locale: ptBR })}
                            </span>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">{grupos.length}</span>
                              <button
                                onClick={() => openDialog(dStr)}
                                className="h-5 w-5 rounded flex items-center justify-center text-muted-foreground hover:text-sucesso hover:bg-muted transition-colors"
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                          {/* Cards agrupados por (funcionario, atividade) */}
                          <div className="space-y-2">
                            {grupos.length === 0 ? (
                              <p className="text-sm text-muted-foreground/60 text-center py-8">—</p>
                            ) : (
                              grupos.map(g => {
                                const func = funcMap.get(g.funcionarioId);
                                const ativ = atividadeMap.get(g.atividadeId ?? "");
                                const nome = func?.apelido ?? func?.nome ?? "?";
                                const cor = avatarCor(func?.id);
                                const setorLabel = formatSetores(g.setorIds.filter(Boolean) as string[], setores);
                                const cfg = STATUS_CFG[g.status] ?? STATUS_CFG.PENDENTE;
                                const opacityCls = g.status === "CONCLUIDA" ? "opacity-70" : g.status === "FALTOU" ? "opacity-65" : "";
                                return (
                                  <div
                                    key={g.key}
                                    onClick={() => openSheet(g.tarefas[0])}
                                    className={`bg-card border border-border rounded-r-xl p-3 cursor-pointer hover:shadow-sm transition-shadow border-l-[3px] ${cfg.border} ${opacityCls}`}
                                  >
                                    <div className="flex items-start gap-2.5">
                                      {/* Avatar */}
                                      <span
                                        className="inline-flex items-center justify-center h-8 w-8 rounded-full text-white text-xs font-bold shrink-0 mt-0.5"
                                        style={{ backgroundColor: cor }}
                                      >
                                        {initials(nome)}
                                      </span>
                                      <div className="flex-1">
                                        <p className="font-semibold text-sm text-foreground leading-snug">{nome}</p>
                                        <p className="text-xs text-muted-foreground leading-snug mt-0.5">
                                          {ativ?.nome ?? "—"}{setorLabel ? ` · ${setorLabel}` : ""}
                                        </p>
                                        {g.tarefas[0].observacoes && (
                                          <p className="text-xs text-muted-foreground mt-0.5 italic">{g.tarefas[0].observacoes}</p>
                                        )}
                                      </div>
                                    </div>
                                    <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.chip}`}>
                                      {cfg.label}
                                    </span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                /* Desktop — Por colaborador: dois blocos horizontais empilhados */
                (() => {
                  const renderColabColumn = (f: typeof funcAtivos[0]) => {
                    const nome = f.apelido ?? f.nome;
                    const cor = avatarCor(f.id);
                    const tColab = tarefas.filter(t => t.funcionarioId === f.id);
                    const totalCards = groupTarefas(tColab).length;
                    const porDia = days
                      .map(d => {
                        const dStr = format(d, "yyyy-MM-dd");
                        const grupos = groupTarefas(tColab.filter(t => t.data === dStr))
                          .sort((a, b) => (a.atividadeId ?? "").localeCompare(b.atividadeId ?? ""));
                        return { d, dStr, grupos };
                      })
                      .filter(x => x.grupos.length > 0);

                    return (
                      <div key={f.id} className="w-56 flex-shrink-0">
                        {/* Header */}
                        <div className="flex items-center gap-2 mb-3 px-1 pb-2 border-b-2 border-border">
                          <span
                            className="inline-flex items-center justify-center h-7 w-7 rounded-full text-white text-xs font-bold shrink-0"
                            style={{ backgroundColor: cor }}
                          >
                            {initials(nome)}
                          </span>
                          <span className="text-sm font-semibold text-foreground truncate flex-1">{nome}</span>
                          <span className="text-xs text-muted-foreground shrink-0">{totalCards}</span>
                          <button
                            onClick={() => openDialog(undefined, f.id)}
                            title="Nova tarefa"
                            className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-sucesso hover:bg-muted transition-colors shrink-0"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => { setShareColabId(f.id); setCopied(false); }}
                            title="Compartilhar plano"
                            className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-sucesso hover:bg-muted transition-colors shrink-0"
                          >
                            <Share2 className="h-3.5 w-3.5" />
                          </button>
                        </div>

                        {/* Seções por dia */}
                        <div className="space-y-3">
                          {tColab.length === 0 ? (
                            <p className="text-sm text-muted-foreground/60 text-center py-6">Sem tarefas</p>
                          ) : (
                            porDia.map(({ d, dStr, grupos }) => (
                              <div key={dStr}>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 px-0.5">
                                  {diaDaSemanaCurto(dStr)} {format(d, "dd")}
                                </p>
                                <div className="space-y-1.5">
                                  {grupos.map(g => {
                                    const ativ = atividadeMap.get(g.atividadeId ?? "");
                                    const setorLabel = formatSetores(g.setorIds.filter(Boolean) as string[], setores);
                                    const cfg = STATUS_CFG[g.status] ?? STATUS_CFG.PENDENTE;
                                    const opacityCls = g.status === "CONCLUIDA" ? "opacity-70" : g.status === "FALTOU" ? "opacity-65" : "";
                                    return (
                                      <div
                                        key={g.key}
                                        onClick={() => openSheet(g.tarefas[0])}
                                        className={`bg-card border border-border rounded-r-xl p-2.5 cursor-pointer hover:shadow-sm transition-shadow border-l-[3px] ${cfg.border} ${opacityCls}`}
                                      >
                                        <p className="font-semibold text-sm text-foreground leading-snug">{ativ?.nome ?? "—"}</p>
                                        {setorLabel && (
                                          <p className="text-xs text-muted-foreground leading-snug mt-0.5">{setorLabel}</p>
                                        )}
                                        {g.tarefas[0].observacoes && (
                                          <p className="text-xs text-muted-foreground mt-0.5 italic">{g.tarefas[0].observacoes}</p>
                                        )}
                                        <span className={`inline-block mt-1.5 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.chip}`}>
                                          {cfg.label}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  };

                  const mid = Math.ceil(funcAtivos.length / 2);
                  const topRow = funcAtivos.slice(0, mid);
                  const bottomRow = funcAtivos.slice(mid);

                  return (
                    <div className="space-y-0">
                      {/* Linha superior */}
                      <div className="overflow-x-auto pb-2">
                        <div className="flex gap-3 min-w-max pb-4">
                          {topRow.map(renderColabColumn)}
                        </div>
                      </div>
                      {/* Divisor */}
                      {bottomRow.length > 0 && (
                        <>
                          <div className="border-t-2 border-dashed border-border mx-0 my-1" />
                          {/* Linha inferior */}
                          <div className="overflow-x-auto pt-2">
                            <div className="flex gap-3 min-w-max pb-4">
                              {bottomRow.map(renderColabColumn)}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()
              )}
            </div>

            {/* ════════════════════════════════════════
                MOBILE (abaixo de md) — layout com tabs e listas
                ════════════════════════════════════════ */}
            <div className="block md:hidden">
              {view === "dia" ? (
                /* Mobile — Por dia */
                <div className="p-4">
                  {/* Chips de resumo */}
                  {Object.entries(statusCounts).length > 0 && (
                    <div className="flex gap-2 flex-wrap mb-3">
                      {Object.entries(statusCounts).map(([status, count]) => {
                        const cfg = STATUS_CFG[status];
                        if (!cfg) return null;
                        return (
                          <span key={status} className={`text-xs px-2 py-1 rounded-full font-medium ${cfg.chip}`}>
                            {count} {cfg.label.toLowerCase()}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {dayTarefas.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-12">Nenhuma tarefa para este dia.</p>
                  ) : (
                    <div className="space-y-2">
                      {dayTarefas.map(t => {
                        const func = funcMap.get(t.funcionarioId);
                        const ativ = atividadeMap.get(t.atividadeId ?? "");
                        const nome = func?.apelido ?? func?.nome ?? "?";
                        const cor = avatarCor(func?.id);
                        const cfg = STATUS_CFG[t.status] ?? STATUS_CFG.PENDENTE;
                        const tIds = t.setorIds?.length ? t.setorIds : (t.setorId ? [t.setorId] : []);
                        const setorLabel = formatSetores(tIds, setores);
                        return (
                          <div
                            key={t.id}
                            onClick={() => openSheet(t)}
                            className={`bg-card border border-border rounded-r-xl p-3 flex items-start gap-2.5 cursor-pointer hover:shadow-sm transition-shadow border-l-[3px] ${cfg.border} ${t.status === "CONCLUIDA" ? "opacity-70" : t.status === "FALTOU" ? "opacity-65" : ""}`}
                          >
                            <span
                              className="inline-flex items-center justify-center h-8 w-8 rounded-full text-white text-xs font-bold shrink-0 mt-0.5"
                              style={{ backgroundColor: cor }}
                            >
                              {initials(nome)}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-foreground truncate">{nome}</p>
                              <p className="text-xs text-muted-foreground truncate">{ativ?.nome ?? "—"}{setorLabel ? ` · ${setorLabel}` : ""}</p>
                              {t.observacoes && (
                                <p className="text-xs text-muted-foreground mt-0.5 italic truncate">{t.observacoes}</p>
                              )}
                            </div>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 mt-0.5 ${cfg.chip}`}>
                              {cfg.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                /* Mobile — Por colaborador */
                <div>
                  {/* Strip de avatares */}
                  <div className="bg-card border-b px-3 py-3">
                    <div className="flex gap-3 overflow-x-auto pb-1">
                      {funcAtivos.map(f => {
                        const nome = f.apelido ?? f.nome;
                        const cor = avatarCor(f.id);
                        const isSelected = f.id === selectedColabId;
                        return (
                          <button
                            key={f.id}
                            onClick={() => setSelectedColabId(f.id)}
                            className="flex flex-col items-center gap-1 shrink-0"
                          >
                            <span
                              className={`inline-flex items-center justify-center h-11 w-11 rounded-full text-white text-sm font-bold transition-all ${isSelected ? "ring-2 ring-ring ring-offset-2" : ""}`}
                              style={{ backgroundColor: cor }}
                            >
                              {initials(nome)}
                            </span>
                            <span className={`text-[10px] max-w-[52px] truncate ${isSelected ? "text-sucesso font-semibold" : "text-muted-foreground"}`}>
                              {nome.split(" ")[0]}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Header de stats */}
                  {selectedColabId && (
                    <div className="px-4 py-3 flex items-center justify-between bg-card border-b">
                      <span className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">{colabFeitas}/{colabTotal}</span> concluídas
                        {colabFaltou > 0 && <span className="text-erro ml-2">· {colabFaltou} faltou</span>}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openDialog(undefined, selectedColabId ?? undefined)}
                          className="flex items-center gap-1.5 text-xs border border-sucesso/30 bg-sucesso-suave text-sucesso rounded-lg px-3 py-1.5 font-medium"
                        >
                          <Plus className="h-3 w-3" />
                          Tarefa
                        </button>
                        <button disabled title="Em breve" className="flex items-center gap-1.5 text-xs border border-border rounded-lg px-3 py-1.5 text-muted-foreground cursor-not-allowed">
                          Compartilhar semana
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Lista por dia */}
                  <div className="p-4 space-y-4">
                    {colabByDay.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-12">Nenhuma tarefa esta semana.</p>
                    ) : (
                      colabByDay.map(({ date, dateStr, tarefas: tDia }) => {
                        const grupos = groupTarefas(tDia)
                          .sort((a, b) => (a.atividadeId ?? "").localeCompare(b.atividadeId ?? ""));
                        return (
                          <div key={dateStr}>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                              {diaDaSemanaCurto(dateStr)} {format(date, "dd")}
                            </p>
                            <div className="space-y-2">
                              {grupos.map(g => {
                                const ativ = atividadeMap.get(g.atividadeId ?? "");
                                const setorLabel = formatSetores(g.setorIds.filter(Boolean) as string[], setores);
                                const cfg = STATUS_CFG[g.status] ?? STATUS_CFG.PENDENTE;
                                const opacityCls = g.status === "CONCLUIDA" ? "opacity-70" : g.status === "FALTOU" ? "opacity-65" : "";
                                return (
                                  <div
                                    key={g.key}
                                    onClick={() => openSheet(g.tarefas[0])}
                                    className={`bg-card border border-border rounded-r-xl p-3 cursor-pointer hover:shadow-sm transition-shadow border-l-[3px] ${cfg.border} ${opacityCls}`}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <p className="font-semibold text-sm text-foreground leading-snug">{ativ?.nome ?? "—"}</p>
                                        {setorLabel && (
                                          <p className="text-xs text-muted-foreground leading-snug mt-0.5">{setorLabel}</p>
                                        )}
                                        {g.tarefas[0].observacoes && (
                                          <p className="text-xs text-muted-foreground mt-0.5 italic">{g.tarefas[0].observacoes}</p>
                                        )}
                                      </div>
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 mt-0.5 ${cfg.chip}`}>
                                        {cfg.label}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── FAB nova tarefa ── */}
      <button
        onClick={() => openDialog(view === "dia" ? format(days[selectedDay], "yyyy-MM-dd") : undefined)}
        className="fixed bottom-5 right-5 w-12 h-12 bg-primary hover:opacity-90 rounded-full flex items-center justify-center shadow-lg transition-opacity z-30"
        aria-label="Nova tarefa"
      >
        <Plus className="text-white w-6 h-6" />
      </button>

      {/* ── Bottom Sheet (mobile + desktop) ── */}
      <TarefaDiariaSheet
        open={activeTarefasDia.length > 0}
        mode="edit"
        tarefasDoDia={activeTarefasDia}
        funcionarios={funcAtivos}
        atividades={atividades}
        setores={setores}
        onClose={() => setActiveTarefasDia([])}
        onSuccess={() => setActiveTarefasDia([])}
      />

      {/* ── Dialog Compartilhar Plano ── */}
      <Dialog open={!!shareColabId} onOpenChange={open => { if (!open) setShareColabId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-4 w-4 text-sucesso" />
              Compartilhar plano
            </DialogTitle>
          </DialogHeader>
          {shareColabId && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Plano formatado para enviar pelo WhatsApp para <strong>{funcMap.get(shareColabId)?.apelido ?? funcMap.get(shareColabId)?.nome}</strong>. Sem valores R$.
              </p>
              {/* Preview estilo WhatsApp */}
              <div className="bg-[#dcf8c6] rounded-xl p-3.5 text-sm text-foreground whitespace-pre-line leading-relaxed shadow-sm font-[system-ui]">
                {shareMessage}
              </div>
              {/* Textarea editável */}
              <Textarea
                value={shareMessage}
                readOnly
                rows={5}
                className="text-xs resize-none font-mono"
              />
            </div>
          )}
          <DialogFooter className="flex-row gap-2 sm:justify-start">
            <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5">
              {copied ? <Check className="h-3.5 w-3.5 text-sucesso" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copiado!" : "Copiar texto"}
            </Button>
            <Button size="sm" onClick={handleWhatsApp} className="gap-1.5 bg-[#25D366] hover:bg-[#1ebe5d] text-white">
              <svg className="h-3.5 w-3.5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Abrir WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dialog Nova Tarefa ── */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nova Tarefa</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Data</Label>
                <Input type="date" value={novaData} onChange={e => setNovaData(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Funcionário</Label>
                <select
                  value={novaFuncId}
                  onChange={e => handleSelectFunc(e.target.value)}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-card appearance-none"
                >
                  <option value="">Selecione...</option>
                  {funcionarios.map(f => (
                    <option key={f.id} value={f.id}>{f.apelido ?? f.nome}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Status inicial */}
            <div className="space-y-1.5">
              <Label>Status inicial</Label>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { value: "PENDENTE" as TarefaStatus,     label: "A fazer",      color: "border-border bg-muted text-foreground"  },
                  { value: "EM_ANDAMENTO" as TarefaStatus, label: "Em andamento", color: "border-atencao/30 bg-atencao-suave text-atencao" },
                  { value: "CONCLUIDA" as TarefaStatus,    label: "Concluída",    color: "border-sucesso bg-sucesso-suave text-sucesso" },
                  { value: "FALTOU" as TarefaStatus,       label: "Faltou",       color: "border-erro/30 bg-erro-suave text-erro"      },
                ] as const).map(s => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setNovaStatus(s.value)}
                    className={`text-xs px-3 py-1.5 rounded-lg border-2 font-medium transition-all text-left ${
                      novaStatus === s.value ? s.color : "border-border bg-muted text-muted-foreground"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Atividade · Setor(es){novaStatus === "FALTOU" && <span className="text-muted-foreground font-normal ml-1">(opcional)</span>}</Label>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={addLinha}>
                  <Plus className="h-3.5 w-3.5 mr-1" />Adicionar linha
                </Button>
              </div>
              {linhas.map((linha, idx) => (
                <div key={idx} className="border border-border rounded-xl p-3 space-y-2">
                  <div className="flex gap-2">
                    <select
                      value={linha.atividadeId}
                      onChange={e => updateLinhaAtiv(idx, e.target.value)}
                      className="flex-1 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-card appearance-none"
                    >
                      <option value="">Atividade *</option>
                      {atividades.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
                    </select>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => removeLinha(idx)} disabled={linhas.length === 1}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div>
                    {linha.setorIds.length > 0 && (
                      <p className="text-xs text-sucesso mb-1">{formatSetores(linha.setorIds, setores)}</p>
                    )}
                    <SetorChipPicker
                      selectedIds={linha.setorIds}
                      onChange={ids => updateLinhaSetorIds(idx, ids)}
                      setores={setores}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Tipo */}
            <div className="space-y-1">
              <Label>Tipo</Label>
              <select
                value={novoTipo}
                onChange={e => {
                  const t = e.target.value;
                  setNovoTipo(t);
                  if (t === "MENSALISTA") setNovaValor("");
                }}
                className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {novoFuncEhMensalista ? (
                  <option value="MENSALISTA">Registro (sem valor)</option>
                ) : (
                  <>
                    <option value="NORMAL">Diária</option>
                    <option value="MEIA_DIARIA">Meia Diária</option>
                    <option value="HORA_EXTRA">Hora Extra</option>
                    <option value="EMPREITA">Empreita</option>
                  </>
                )}
              </select>
            </div>

            {novoTipo === "MENSALISTA" ? (
              <div className="rounded-xl border border-info/30 bg-info-suave px-3 py-2.5">
                <p className="text-sm font-medium text-info">🔵 Registro (sem valor)</p>
                <p className="text-xs text-info mt-0.5">
                  Mensalista: registra o dia e o ciclo, não entra na folha da quinzena.
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                <Label>Valor da diária (do dia)</Label>
                <Input type="number" step="0.01" min="0" placeholder="0.00" value={novaValor} onChange={e => setNovaValor(e.target.value)} />
              </div>
            )}

            <div className="space-y-1">
              <Label>Observações <span className="text-muted-foreground font-normal">(opcional)</span></Label>
              <Textarea value={novaObs} onChange={e => setNovaObs(e.target.value)} placeholder="Instruções para o colaborador..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancelar</Button>
            <Button onClick={handleAddTarefas} disabled={!novaFuncId || (novaStatus !== "FALTOU" && linhas.every(l => !l.atividadeId)) || createTarefa.isPending}>
              {createTarefa.isPending ? "Salvando..." : novaStatus === "FALTOU" && linhas.every(l => !l.atividadeId) ? "Registrar falta" : `Criar tarefa${linhas.filter(l => l.atividadeId && l.setorIds.length > 0).length > 1 ? "s" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
